<template>
  <div class="add_trading">
    <header>
      <div class="text-24 text-bold mb-6">
        {{ $t('Label.New Trading') }}
      </div>
    </header>
    <section style="padding-bottom: 6px">
      <el-form :model="queries" :rules="rules" ref="formOwnerSectionId">
        <el-form-item :label="$t('Label.Owner')" prop="ownerSectionId">
          <el-select
            v-model="queries.ownerSectionId"
            filterable
            popper-class="owner-section"
          >
            <el-option
              v-for="owner in searchCriteriaOwnerSections"
              :key="owner.sectionId"
              :label="owner.sectionShortName"
              :value="owner.sectionId"
            />
          </el-select>
        </el-form-item>
      </el-form>
    </section>
    <section>
      <div class="text-18 mb-4 cursor-pointer text-bold" @click="openToggle">
        <img
          class="arrow"
          src="@/assets/images/icons/accordion_arrow.svg"
          :class="{ open: openForm }"
        />{{ $t('Label.Filter') }}
      </div>
      <transition
        name="content"
        @enter="enter"
        @after-enter="afterEnter"
        @leave="leave"
        @after-leave="afterLeave"
      >
        <div v-show="openForm">
          <table>
            <tr>
              <th>{{ $t('Label.Trading Type') }}</th>
              <td>
                <el-radio-group v-model="queries.tradeTypeCd">
                  <el-radio
                    v-for="item in tradeTypes"
                    :key="item.code"
                    :label="item.code"
                    border
                    >{{ item.label }}</el-radio
                  >
                </el-radio-group>
              </td>
            </tr>
            <tr>
              <th>{{ $t('Label.Payment Terms') }}</th>
              <td>
                <el-radio-group v-model="queries.paymentTermsCd">
                  <el-radio
                    v-for="item in PAYMENT_TERMS_VARIABLES"
                    :key="item.code"
                    :label="item.code"
                    border
                    >{{ item.label }}</el-radio
                  >
                </el-radio-group>
              </td>
            </tr>
            <tr>
              <th>{{ $t('Label.Trade Terms') }}</th>
              <td>
                <el-select
                  v-model="queries.tradeTermsCd"
                  placeholder="Select"
                  clearable
                >
                  <el-option
                    v-for="item in DELIVERY_TERMS_VARIABLES"
                    :key="item.code"
                    :label="item.label"
                    :value="item.code"
                  >
                  </el-option>
                </el-select>
              </td>
            </tr>
            <tr>
              <th>{{ $t('Label.Mode of Transport') }}</th>
              <td>
                <el-radio-group v-model="queries.modeOfTransportCd">
                  <el-radio
                    v-for="item in MODE_OF_TRANSPORT_VARIABLES"
                    :key="item.code"
                    :label="item.code"
                    class="mode_of_transport"
                    border
                    ><i :class="[classNames[item.code]]" />{{
                      item.label
                    }}</el-radio
                  >
                </el-radio-group>
              </td>
            </tr>
          </table>
        </div>
      </transition>
    </section>
    <footer>
      <el-form :model="form" :rules="rules" ref="form">
        <el-form-item
          :label="$t('Label.Base Trading Pattern')"
          prop="tradingPattern"
          style="margin-bottom: 24px"
        >
          <el-select
            v-model="form.tradingPattern"
            placeholder="Select"
            filterable
            @change="setTradingName"
          >
            <el-option
              v-for="(pattern, index) in filteredPatterns"
              :key="pattern.tradingPatternId + index"
              :label="pattern.tradingPatternName"
              :value="pattern.tradingPatternId"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          :label="$t('Label.Trading Name')"
          prop="tradingName"
          style="margin-bottom: 24px"
        >
          <el-input
            type="text"
            v-model="form.tradingName"
            placeholder="Trading Name"
            show-word-limit
            maxlength="100"
          ></el-input>
        </el-form-item>
      </el-form>
      <div style="display: flex;justify-content: flex-end">
        <tw-button id="kc-login" type="primary" size="medium" @click="next"
          >Next</tw-button
        >
      </div>
    </footer>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  onMounted,
  getCurrentInstance,
} from '@vue/composition-api';
import _ from 'lodash';
import { $api } from '@/store/ApiClient';
import { LakeelValidation } from 'lib-lakeel-validation';
import {
  DELIVERY_TERMS_VARIABLES,
  MODE_OF_TRANSPORT_VARIABLES,
  PAYMENT_TERMS_VARIABLES,
  TRADE_TYPE_VARIABLES,
  TRADE_TYPE,
} from 'lib-tw-common';

import { collapseTransition } from '@/utils/nextFrame';

import TwButton from '@/components/atoms/TwButton.vue';

const LAKEEL_SYNERGY_LOGIC_HOSTNAME =
  process.env.VUE_APP_LAKEEL_SYNERGY_LOGIC_HOSTNAME;
export default {
  name: 'TwAddTrading',
  mixins: [collapseTransition],
  components: {
    TwButton,
  },
  props: {
    history: Object,
  },
  setup(props, { root }) {
    const { proxy } = getCurrentInstance();
    const form = reactive({
      tradingPattern: null,
      tradingName: null,
    });
    const queries = reactive({
      ownerSectionId: null,
      tradeTypeCd: null,
      paymentTermsCd: null,
      tradeTermsCd: null,
      modeOfTransportCd: null,
    });
    const data = ref({});
    const prevData = ref(false);
    const validResult = ref({});
    const validationService = new LakeelValidation(
      LAKEEL_SYNERGY_LOGIC_HOSTNAME,
    );
    const openForm = ref(false);
    const classNames = {
      0: 'bulk',
      1: 'container',
      2: 'air',
    };
    const rules = {
      ownerSectionId: [
        {
          lakeelMessageSourceFlg: true,
          validationId: 'tw-isNotEmpty',
          validator: onEventValidation,
          trigger: 'change',
        },
      ],
      tradingPattern: [
        {
          lakeelMessageSourceFlg: true,
          validationId: 'tw-isNotEmpty',
          validator: onEventValidation,
          trigger: 'change',
        },
      ],
      tradingName: [
        {
          lakeelMessageSourceFlg: true,
          validationId: 'tw-isMaxLength100',
          validator: onEventValidation,
          trigger: 'change',
        },
      ],
    };
    const searchCriteriaOwnerSections = computed(
      () => _.get(data.value, 'searchCriteriaOwnerSections') || [],
    );
    const baseTradingPatterns = computed(() =>
      _.reject(_.get(data.value, 'baseTradingPatterns') || [], o => {
        if (proxy.$store.getters.isIntermediate) return false;
        return o.tradeTypeCd === TRADE_TYPE.OFF_SHORE;
      }),
    );
    const filteredPatterns = computed(() => {
      const q = queries;
      return _.filter(
        baseTradingPatterns.value,
        o =>
          (!q.ownerSectionId || o.ownerSectionId === q.ownerSectionId) &&
          (!q.tradeTypeCd || o.tradeTypeCd === q.tradeTypeCd) &&
          (!q.paymentTermsCd ||
            o.paymentTermsCd === q.paymentTermsCd ||
            o.extraPaymentTermsCd === q.paymentTermsCd) &&
          (!q.tradeTermsCd ||
            o.tradeTermsCd === q.tradeTermsCd ||
            o.extraTradeTermsCd === q.tradeTermsCd) &&
          (q.modeOfTransportCd === null ||
            q.modeOfTransportCd === undefined ||
            o.modeOfTransportCd === q.modeOfTransportCd ||
            o.extraModeOfTransportCd === q.modeOfTransportCd),
      );
    });
    const tradeTypes = computed(() => {
      return proxy.$store.getters.isIntermediate
        ? TRADE_TYPE_VARIABLES
        : _.take(TRADE_TYPE_VARIABLES, 2);
    });

    const fetch = async () => {
      const params = {
        lslConfig: {
          serviceCode: 'tw-transaction-bff-api',
          apiCode: 'get_/v1/tradings/{companyId}/manages/drawer',
          path: { companyId: proxy.$store.state.companyId },
        },
      };
      try {
        const res = await $api.request(params);
        data.value = res;
        if (prevData.value) {
          Object.assign(form, _.clone(props.history.form));
          Object.assign(queries, _.clone(props.history.queries));
          prevData.value = false;
        }
      } catch (err) {
        proxy.$store.dispatch('SHOW_ALERT', err.message);
      }
    };

    async function onEventValidation(rule) {
      const refName =
        rule.field === 'ownerSectionId' ? 'formOwnerSectionId' : 'form';
      await validationService.lakeelValidAll.call(
        proxy,
        {
          formName: refName,
          auto: true,
          field: 'validResult',
          lslHostName: LAKEEL_SYNERGY_LOGIC_HOSTNAME,
          relationMsgDisplayType: 'second',
        },
        rule.field,
      );
    }

    async function validationCheck(refName) {
      await validationService.lakeelValidAll.call(proxy, {
        formName: refName,
        auto: true,
        field: 'validResult',
        lslHostName: LAKEEL_SYNERGY_LOGIC_HOSTNAME,
        relationMsgDisplayType: 'second',
      });
      return !validResult.value.isError;
    }
    async function setTradingName() {
      form.tradingName = _.get(
        _.find(filteredPatterns.value, {
          tradingPatternId: form.tradingPattern,
        }),
        'tradingPatternName',
      );
    }

    async function next() {
      const check1 = await validationCheck('form');
      const check2 = await validationCheck('formOwnerSectionId');
      const isValid = check1 && check2;
      if (isValid) {
        const pattern = _.find(filteredPatterns.value, {
          tradingPatternId: form.tradingPattern,
        });
        const section = _.find(searchCriteriaOwnerSections.value, {
          sectionId: queries.ownerSectionId,
        });
        const params = {
          tradingPatternId: form.tradingPattern,
          tradingName: form.tradingName || pattern?.tradingPatternName,
          originTradingPatternName: pattern?.tradingPatternName,
          ownerSectionId: queries.ownerSectionId,
          ownerSectionName: section?.sectionShortName,
          history: {
            form: {
              tradingPattern: form.tradingPattern,
              tradingName: form.tradingName || pattern?.tradingPatternName,
            },
            queries,
            name: proxy.$route.name,
          },
        };
        console.log(params);
        const key = `new-${Date.now()}`;
        sessionStorage.setItem(key, JSON.stringify(params));
        root.$router.push({ path: '/trading/process/new/1', query: { p: key } });
      } else {
        proxy.$store.dispatch(
          'SHOW_ALERT',
          root.$t('Message.ERROR__INPUT_INVALID'),
        );
      }
    }
    function openToggle() {
      openForm.value = !openForm.value;
    }
    onMounted(() => {
      if (props.history) prevData.value = true;
      fetch();
    });
    return {
      DELIVERY_TERMS_VARIABLES,
      MODE_OF_TRANSPORT_VARIABLES,
      form,
      PAYMENT_TERMS_VARIABLES,
      queries,
      rules,
      searchCriteriaOwnerSections,
      filteredPatterns,
      tradeTypes,
      setTradingName,
      next,
      openForm,
      openToggle,
      validResult,
      classNames,
    };
  },
};
</script>
